import { BUDGET_BANDS } from "@/lib/pricing-floors";

/**
 * The questions asked before a call is booked, and the brief they turn into.
 *
 * WHY THIS EXISTS. Until now `/book` loaded a calendar and asked nothing. Somebody could take a
 * slot in the week and arrive as a name and an email address, so the first ten minutes of a thirty
 * minute call went on things a form answers in twenty seconds. Tom, 2026-09-02: *"we don't know
 * anything about them before we get on the call. The more information we have, the better, because
 * then we can tailor the call to them. We can do a bit of research beforehand."*
 *
 * THE RULE THE QUESTION SET IS BUILT ON: every question either changes how we prepare or it does
 * not go in. Business name and website let someone actually look at them before the call. Trading
 * length and team size say what kind of business is on the other end. What they want to talk about
 * decides who should be on the call and what to have open. Budget decides whether the call is a
 * scoping conversation or a polite no. Nothing here is asked because it is interesting.
 *
 * THE OTHER RULE: four required fields, and the rest are one tap. Every extra required box is a
 * booking that does not happen, and a chip nobody has to think about is answered by almost
 * everybody. Plain English throughout, no ranges written with a dash, and no jargon a business
 * owner would have to decode ("revenue model", "tech stack", "pain points" are all banned here).
 */

export type PreCallChoiceQuestion = {
  key: PreCallChoiceKey;
  /** The question as a person would ask it out loud. */
  prompt: string;
  /** Shown under the prompt when the reason for asking is not obvious. */
  hint?: string;
  options: readonly string[];
  /** True when more than one answer can be true at once. */
  multi?: boolean;
};

export type PreCallChoiceKey =
  | "tradingLength"
  | "teamSize"
  | "wantToTalkAbout"
  | "budget"
  | "bestWayToReach";

export const PRE_CALL_QUESTIONS: readonly PreCallChoiceQuestion[] = [
  {
    key: "wantToTalkAbout",
    prompt: "What do you want to talk about?",
    hint: "Tick anything that applies. Not sure yet is a real answer.",
    multi: true,
    options: [
      "A new website",
      "Taking bookings or payments online",
      "A system to run the business",
      "An app",
      "Fixing something that already exists",
      "Not sure yet",
    ],
  },
  {
    key: "tradingLength",
    prompt: "How long have you been running?",
    options: ["Under a year", "1 to 3 years", "3 to 10 years", "More than 10 years"],
  },
  {
    key: "teamSize",
    prompt: "How many people work in the business?",
    options: ["Just me", "2 to 5", "6 to 20", "More than 20"],
  },
  {
    key: "budget",
    prompt: "What budget are you working with?",
    hint: "Pick the closest. It tells us what we can realistically build for you.",
    /* The SAME bands as the enquiry form, from lib/pricing-floors.ts, so one prospect answering
       both never looks like two different buyers.

       REQUIRED, and "Not sure yet" is deliberately left off. Tom, 2026-09-25: "We can't have no
       budget because they're coming to us, and then they're expecting it to be like £500, and
       it's an app." A skippable question let people with no idea of cost book a call about an
       app, so every booking now carries a band, and the lowest one ("Under £5,000") is still
       an honest answer for anyone who has not thought about it. */
    options: BUDGET_BANDS.filter((b) => b.value !== "unsure").map((b) => b.label),
  },
  {
    key: "bestWayToReach",
    prompt: "Best way to reach you?",
    multi: true,
    options: ["Phone", "Email", "WhatsApp"],
  },
] as const;

/** Only these two choices stand between a visitor and the calendar. */
export const BOOKING_GATE_QUESTIONS = PRE_CALL_QUESTIONS.filter(
  (question) => question.key === "wantToTalkAbout" || question.key === "budget",
);

/** The free-text question. One box, one line, optional, and the most useful answer on the form. */
export const PRE_CALL_TIME_SINK = {
  key: "timeSink" as const,
  prompt: "What is eating the most time right now?",
  hint: "One line is plenty. Whatever you would fix first if you could.",
  placeholder: "Quotes get typed out twice, once in email and once in the accounts",
  maxLength: 700,
};

/**
 * "How did you hear about us?", asked on the page and never again in Calendly.
 *
 * WHY. Tom, 2026-09-23, after a call booked from New York with nothing to say how they found us:
 * *"I need to know where they found us."* Calendly only asks for a phone number and a notes box,
 * so the answer rides into the notes line (`preCallCalendlyNote`) and Lead Engine reads it back
 * out of the booking. Nobody is asked the same thing twice.
 *
 * SHAPE. One dropdown of channels, then a second set of choices for the channel picked, because
 * "social media" or "AI" on its own does not say which one to spend on. Ads and directories are
 * listed now although nothing is running yet: the list has to be right before the first ad goes
 * live, not after, or the first month of spend reads as "Something else". A few channels also
 * take one line of free text (who recommended us, which site we built), always optional.
 *
 * The stored answer is the words they saw, "An AI assistant: ChatGPT", so a report reads as English.
 * Lead Engine keys on the `Found us:` prefix in the note. Change the prefix and change both.
 */
export type HeardAboutSource = {
  label: string;
  /** The second set of choices, shown once this channel is picked. */
  options?: readonly string[];
  /** A one-line optional follow-up in their own words. */
  detail?: { prompt: string; placeholder: string };
};

export const HEARD_ABOUT_SOURCES: readonly HeardAboutSource[] = [
  {
    label: "Google",
    options: ["Google search", "Google Maps", "Google reviews"],
  },
  {
    label: "An AI assistant",
    options: [
      "ChatGPT",
      "Gemini",
      "Claude",
      "Perplexity",
      "Microsoft Copilot",
      "Grok",
      "Meta AI",
      "Another AI",
    ],
  },
  {
    label: "Social media",
    options: [
      "Facebook",
      "Instagram",
      "LinkedIn",
      "TikTok",
      "YouTube",
      "X (Twitter)",
      "Threads",
      "Reddit",
      "Pinterest",
      "Somewhere else",
    ],
  },
  {
    label: "A review or directory site",
    options: [
      "Clutch",
      "Trustpilot",
      "DesignRush",
      "GoodFirms",
      "Sortlist",
      "Bark",
      "Yell",
      "Checkatrade",
      "Upwork",
      "Somewhere else",
    ],
  },
  {
    label: "An advert",
    options: [
      "Google",
      "Facebook or Instagram",
      "LinkedIn",
      "TikTok",
      "YouTube",
      "Somewhere else",
    ],
  },
  {
    label: "Word of mouth or a referral",
    options: [
      "A client of Ownly",
      "A friend or family member",
      "A business contact",
      "My accountant",
      "Someone else",
    ],
    detail: { prompt: "Who was it? So we can thank them.", placeholder: "Their name or business" },
  },
  {
    label: "A website Ownly built",
    detail: { prompt: "Which one, if you remember?", placeholder: "The business or web address" },
  },
  {
    label: "Another search engine",
    options: ["Bing", "DuckDuckGo", "Yahoo", "Somewhere else"],
  },
  { label: "An email from Ownly" },
  { label: "A phone call from Ownly" },
  { label: "GrowthSync" },
  {
    label: "An event or networking",
    detail: { prompt: "Which one?", placeholder: "The event or group" },
  },
  {
    label: "Something else",
    detail: { prompt: "Where was it?", placeholder: "Tell us in a few words" },
  },
  { label: "I can't remember" },
] as const;

export const HEARD_ABOUT_PROMPT = "How did you hear about us?";

/** How the note labels the answer. Lead Engine reads it back by this exact prefix. */
export const HEARD_ABOUT_NOTE_PREFIX = "Found us:";

/**
 * One answer as a single line: "Social media: Instagram", "Word of mouth: A client of Ownly (Jo at
 * Harrow Dental)". Returns undefined when no channel was picked.
 */
export function heardAboutAnswer(
  source: string | undefined,
  option?: string,
  detail?: string,
): string | undefined {
  const channel = source?.trim();
  if (!channel) return undefined;
  const pick = option?.trim();
  /* The note splits on " | ", so a pipe typed into the free text must not split the answer. */
  const said = detail?.replace(/\|/g, "/").replace(/\s+/g, " ").trim().slice(0, 120);
  let line = pick ? `${channel}: ${pick}` : channel;
  if (said) line += pick ? ` (${said})` : `: ${said}`;
  return line;
}

/**
 * The phone number typed on /book, in the form Calendly's phone box reads ("+447700900123").
 *
 * Asked on the form since 2026-09-28 so the rep has a number to call if a booking changes or they
 * do not turn up, and so the calendar's own required phone box is already filled. (It was briefly
 * mobile-only, for booking texts that Tom then dropped the same evening; landlines are fine again.)
 */
export function bookingPhone(raw: string): { ok: true; value: string } | { ok: false; reason: "missing" | "invalid" } {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, reason: "missing" };
  let digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("00")) digits = `+${digits.slice(2)}`;
  if (!digits.startsWith("+")) {
    const national = digits.replace(/\D/g, "");
    if (national.startsWith("0")) digits = `+44${national.slice(1)}`;
    else if (national.startsWith("44")) digits = `+${national}`;
    else if (national.startsWith("7") && national.length === 10) digits = `+44${national}`;
    else return { ok: false, reason: "invalid" };
  }
  const e164 = `+${digits.slice(1).replace(/\D/g, "")}`;
  if (e164.startsWith("+44")) return /^\+44[1-9]\d{8,9}$/.test(e164) ? { ok: true, value: e164 } : { ok: false, reason: "invalid" };
  return /^\+\d{8,15}$/.test(e164) ? { ok: true, value: e164 } : { ok: false, reason: "invalid" };
}

/**
 * WHEN THEY WANT IT, AND THE TWO CONSENTS (Tom, 2026-10-11).
 *
 * Timeline is optional and sits under the budget question rather than as a step of its own, so the
 * path to the calendar is no longer than it was. The ids are a contract with Lead Engine, which
 * reads `timeline` by these exact values; the labels are what the visitor sees and can change.
 */
export const TIMELINE_OPTIONS = [
  { id: "asap", label: "As soon as possible" },
  { id: "1_3_months", label: "In 1 to 3 months" },
  { id: "3_6_months", label: "In 3 to 6 months" },
  { id: "exploring", label: "Just exploring" },
] as const;

export type TimelineId = (typeof TIMELINE_OPTIONS)[number]["id"];

export function isTimelineId(value: unknown): value is TimelineId {
  return typeof value === "string" && TIMELINE_OPTIONS.some((o) => o.id === value);
}

export function timelineLabel(id: string | undefined): string | undefined {
  return TIMELINE_OPTIONS.find((o) => o.id === id)?.label;
}

/**
 * The budget answer as a stable id for Lead Engine (`budgetRange`), alongside the words in `budget`.
 *
 * Built from the SAME bands the question shows (lib/pricing-floors.ts), so it can never name a band
 * the visitor was not offered. Hyphens become underscores to match the id style of `timeline`:
 * under_5k, 5k_15k, 15k_35k, 35k_plus.
 */
export const BUDGET_RANGE_IDS = BUDGET_BANDS.filter((b) => b.value !== "unsure").map((b) => ({
  id: b.value.replace(/-/g, "_"),
  label: b.label as string,
}));

export function budgetRangeFor(label: string | undefined): string | undefined {
  const wanted = label?.trim();
  return BUDGET_RANGE_IDS.find((b) => b.label === wanted)?.id;
}

/** Copy for the consent boxes, in one place so the form and the privacy policy say the same thing. */
export const EMAIL_OPT_IN_LABEL =
  "Send me a few helpful emails about my project. I can unsubscribe at any time.";
export const WHATSAPP_OPT_IN_LABEL = "Message me on WhatsApp about my enquiry.";

/** The optional "what do you want built or fixed" box, asked under the first question. */
export const WANTED_OUTCOME = {
  key: "wantedOutcome" as const,
  prompt: "Anything specific you want built or fixed?",
  placeholder: "A booking system that takes deposits, so no-shows stop costing us",
  maxLength: 700,
};

export type PreCallAnswers = {
  /** Who and where, so somebody can look them up before the call. */
  name: string;
  businessName: string;
  email: string;
  phone: string;
  websiteUrl?: string;
  /** Chip answers, joined with ", " where the question takes more than one. */
  wantToTalkAbout?: string;
  tradingLength?: string;
  teamSize?: string;
  budget?: string;
  bestWayToReach?: string;
  timeSink?: string;
  /** "How did you hear about us?", already joined into one line by `heardAboutAnswer`. */
  heardAbout?: string;
  /** Free text: what they want built or fixed. Optional. */
  wantedOutcome?: string;
  /** One of TIMELINE_OPTIONS' ids. Optional. */
  timeline?: TimelineId;
  /** Ticked "a few helpful emails about my project". Never true unless they ticked it. */
  emailOptIn?: boolean;
  /** Ticked "message me on WhatsApp". Only offered once a phone number is typed. */
  whatsappOptIn?: boolean;
};

/** Field caps, mirrored server-side. Long enough for a real answer, short enough to bound a POST. */
export const PRE_CALL_MAX_LENGTHS: Record<string, number> = {
  name: 120,
  businessName: 200,
  email: 254,
  phone: 40,
  websiteUrl: 2048,
  wantToTalkAbout: 300,
  tradingLength: 60,
  teamSize: 60,
  budget: 60,
  bestWayToReach: 60,
  timeSink: 700,
  heardAbout: 300,
  wantedOutcome: 700,
};

/**
 * The brief, as a rep reads it.
 *
 * Ordered the way somebody prepares rather than the way the form was filled in: who they are and
 * how to reach them, then what the call is about, then the two lines that decide how to pitch it.
 * "Not given" is written out rather than left blank, because a blank line in a brief reads as a
 * question nobody asked rather than one they skipped.
 */
export function preCallBriefRows(answers: PreCallAnswers): { label: string; value: string }[] {
  const given = (v: string | undefined) => (v && v.trim() ? v.trim() : "Not given");
  return [
    { label: "Business", value: given(answers.businessName) },
    { label: "Website", value: given(answers.websiteUrl) },
    { label: "Contact", value: given(answers.name) },
    { label: "Email", value: given(answers.email) },
    { label: "Phone", value: given(answers.phone) },
    { label: "Best way to reach them", value: given(answers.bestWayToReach) },
    { label: "Wants to talk about", value: given(answers.wantToTalkAbout) },
    { label: "Wants built or fixed", value: given(answers.wantedOutcome) },
    { label: "Trading", value: given(answers.tradingLength) },
    { label: "People in the business", value: given(answers.teamSize) },
    { label: "Budget in mind", value: given(answers.budget) },
    { label: "When they want it", value: given(timelineLabel(answers.timeline)) },
    { label: "Eating the most time", value: given(answers.timeSink) },
    { label: "Found us through", value: given(answers.heardAbout) },
    /* Written out both ways, because "no" is an answer a rep must respect, not a blank. */
    { label: "Helpful emails", value: answers.emailOptIn ? "Yes, opted in" : "No" },
    { label: "WhatsApp messages", value: answers.whatsappOptIn ? "Yes, opted in" : "No" },
  ];
}

/** The same brief as plain text, for the lead record in the CRM and the plain-text email part. */
export function preCallBriefText(answers: PreCallAnswers): string {
  return preCallBriefRows(answers)
    .map((row) => `${row.label}: ${row.value}`)
    .join("\n");
}

/**
 * What rides into Calendly's own "anything we should know" answer.
 *
 * Short on purpose. The full brief goes to the CRM and to the team inbox; this is the line that
 * shows up in the calendar invite itself, where a wall of text is worse than three facts.
 */
export function preCallCalendlyNote(answers: PreCallAnswers): string {
  const parts = [
    answers.businessName?.trim() || null,
    answers.wantToTalkAbout?.trim()
      ? `Wants to discuss: ${answers.wantToTalkAbout.trim()}`
      : null,
    answers.budget?.trim() ? `Budget: ${answers.budget.trim()}` : null,
    timelineLabel(answers.timeline) ? `When: ${timelineLabel(answers.timeline)}` : null,
    /* Before the free text, so a long time-sink answer can never push it past the 500 cut. */
    answers.heardAbout?.trim() ? `${HEARD_ABOUT_NOTE_PREFIX} ${answers.heardAbout.trim()}` : null,
    answers.timeSink?.trim() ? `Time sink: ${answers.timeSink.trim()}` : null,
  ].filter((p): p is string => Boolean(p));
  return parts.join(" | ").slice(0, 500);
}

/**
 * The answers as Lead Engine reads them: flat, keyed by question, no nulls.
 *
 * THE KEYS ARE A CONTRACT WITH ANOTHER REPOSITORY. `lead-engine/src/lib/qualification/questions.ts`
 * reads exactly this list and ignores everything else, so a key renamed on one side and not the
 * other does not fail: it silently files nothing, and the first sign is a rep opening a scope page
 * that starts blank for a business that answered every question. Change one, change both.
 *
 * The same answers also travel inside `notes` as prose, because a person reading the lead wants a
 * paragraph and a program wants fields. Neither is a substitute for the other.
 */
export function preCallQualificationFields(answers: PreCallAnswers): Record<string, string> {
  const fields: Record<string, string> = {};
  const keys = [
    "wantToTalkAbout",
    "tradingLength",
    "teamSize",
    "budget",
    "bestWayToReach",
    "timeSink",
    "heardAbout",
  ] as const;
  for (const key of keys) {
    const value = answers[key]?.trim();
    if (value) fields[key] = value;
  }
  return fields;
}
